import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Clock, Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AUTH_TYPE_LABELS, type AuthTypeName } from "../../../../../../shared/index";

const AUTH_TYPES: AuthTypeName[] = ["full", "limited", "denied", "singleFeature"];

/**
 * The clients list.
 *
 * Everything here answers a question the legacy screen could not: it filtered the whole
 * list in memory on the client name alone, showed the API token in plain text in a
 * grid column, and gave no sign that a client had no grants at all. Search runs on the
 * server, the token never appears, and the states that break a customer are badges.
 */
export default function B2bClientsList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(params.get("q") ?? "");

  const filters = useMemo(
    () => ({
      q: params.get("q") ?? undefined,
      active:
        params.get("active") === null ? undefined : params.get("active") === "true",
      authType: params.get("authType") ?? undefined,
      withoutGrants: params.get("withoutGrants") === "true" || undefined,
      page: Number(params.get("page") ?? 1),
    }),
    [params]
  );

  const clients = useQuery({
    queryKey: ["b2b", "clients", filters],
    queryFn: () => api.b2b.listClients(filters),
  });

  // `/` focuses search from anywhere on the page — this list is the section's home and
  // an operator usually arrives knowing the client's name.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "/" && document.activeElement?.tagName !== "INPUT") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">API clients</h1>
          <p className="text-sm text-muted-foreground">
            {clients.data ? `${clients.data.total} clients` : "Loading…"}
          </p>
        </div>
        <Button onClick={() => navigate("/dashboard/b2b/clients/new")}>
          <Plus className="mr-2 h-4 w-4" />
          New client
        </Button>
      </div>

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setParam("q", query);
        }}
      >
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by client key…  ( / )"
            className="w-72 pl-8"
          />
        </div>
        <Button type="submit" variant="secondary">
          Search
        </Button>

        <select
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={params.get("authType") ?? ""}
          onChange={(event) => setParam("authType", event.target.value || null)}
        >
          <option value="">All access types</option>
          {AUTH_TYPES.map((type) => (
            <option key={type} value={type}>
              {AUTH_TYPE_LABELS[type]}
            </option>
          ))}
        </select>

        <select
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={params.get("active") ?? ""}
          onChange={(event) => setParam("active", event.target.value || null)}
        >
          <option value="">Active and inactive</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={params.get("withoutGrants") === "true"}
            onChange={(event) => setParam("withoutGrants", event.target.checked ? "true" : null)}
          />
          Broken only (no grants)
        </label>
      </form>

      <div className="rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client key</TableHead>
              <TableHead>Access type</TableHead>
              <TableHead className="text-right">Grants</TableHead>
              <TableHead>Tenant</TableHead>
              <TableHead>Environment</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {clients.data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground">
                  No clients match these filters.
                </TableCell>
              </TableRow>
            )}
            {clients.data?.items.map((client) => (
              <TableRow key={client.id} className="cursor-pointer">
                <TableCell>
                  <Link
                    to={`/dashboard/b2b/clients/${client.id}`}
                    className="font-medium hover:underline"
                  >
                    {client.key}
                  </Link>
                </TableCell>
                <TableCell className="text-sm">{AUTH_TYPE_LABELS[client.authType]}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {client.authType === "full" ? "all" : client.grantCount}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {client.tenantId ?? "—"}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {client.environment ?? "—"}
                </TableCell>
                {/* From Application Insights, which retains 90 days. A client with grants
                    and nothing in that window is either mis-integrated or gone — worth
                    saying out loud rather than showing an em dash. */}
                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                  {client.lastSeenAt ? (
                    new Date(client.lastSeenAt).toLocaleDateString()
                  ) : (
                    <span title="No request in the last 90 days">never used</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {!client.isActive && <Badge variant="secondary">inactive</Badge>}
                    {client.hasNoGrants && (
                      <Badge variant="destructive" title="This client gets 401 on every endpoint">
                        <AlertTriangle className="mr-1 h-3 w-3" />
                        no grants
                      </Badge>
                    )}
                    {client.propagationPending && (
                      <Badge variant="outline" title="A cache eviction has not been confirmed">
                        <Clock className="mr-1 h-3 w-3" />
                        pending
                      </Badge>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {clients.data && clients.data.total > clients.data.pageSize && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {clients.data.page} of {Math.ceil(clients.data.total / clients.data.pageSize)}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={clients.data.page <= 1}
              onClick={() => setParams({ ...paramsToObject(params), page: String(clients.data!.page - 1) })}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={clients.data.page * clients.data.pageSize >= clients.data.total}
              onClick={() => setParams({ ...paramsToObject(params), page: String(clients.data!.page + 1) })}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function paramsToObject(params: URLSearchParams): Record<string, string> {
  return Object.fromEntries(params.entries());
}
